import { apiClient } from "@lib/axios";
import { GenericAbortSignal } from "axios";
import type { TAnalyticsSelection, TGetAnalyticsData } from "./types";

const cleanParams = (params: TGetAnalyticsData["params"]) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  );

export const analyticsApi = {
  async getAnalyticsData(
    { query, params }: TGetAnalyticsData,
    signal?: GenericAbortSignal,
  ) {
    const response = await apiClient.get<Record<string, unknown>>(
      "/analytics",
      {
        params: {
          query: query.join(","),
          ...cleanParams(params),
        },
        signal: signal as GenericAbortSignal,
      },
    );
    return response;
  },
  /**
   * Resolves the titles (and the version's creation date) behind a deep link's
   * `?story=`/`?version=` ids. Reads MongoDB, so unlike the ClickHouse-backed
   * queries above it is not bounded by the selected date window.
   */
  async getSelection({
    storyId,
    versionId,
  }: {
    storyId?: string;
    versionId?: string;
  }) {
    return apiClient.get<TAnalyticsSelection>("/analytics/selection", {
      params: {
        ...(storyId && { storyId }),
        ...(versionId && { versionId }),
      },
    });
  },
  /**
   * Delete/archive sessions by versionId (archive all sessions) or by sessionId (archive/delete one session).
   * At least one of `versionId` or `sessionId` must be provided.
   */
  async deleteSessions({
    versionId,
    sessionId,
    dateFrom,
    dateTo,
  }: {
    versionId?: string;
    sessionId?: string;
    dateFrom: string;
    dateTo: string;
  }) {
    if (!versionId && !sessionId) {
      throw new Error("Either versionId or sessionId must be provided.");
    }
    const params: Record<string, string> = {
      dateFrom,
      dateTo,
    };
    if (versionId) params.versionId = versionId;
    if (sessionId) params.sessionId = sessionId;

    const response = await apiClient.delete<Record<string, unknown>>(
      "/analytics/sessions",
      {
        params,
      },
    );
    return response;
  },
};
